<script setup lang="ts">
import InputNumber from 'primevue/inputnumber';
import { useField } from 'vee-validate';
import { computed, nextTick, onMounted, reactive, watch } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';

import {
  InputRangeNumberEmits,
  InputRangeNumberProps,
} from './InputRangeNumber.vue.d';
import FieldWrapper from '../fieldwrapper/FieldWrapper.vue';
import { FieldValidation } from '../form/Form.vue.d';
import InputGroup from '../inputgroup/InputGroup.vue';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

const props = defineProps<InputRangeNumberProps>();
const emit = defineEmits<InputRangeNumberEmits>();

const { locale } = useWangsVueConfig('InputRangeNumber');

// Separate fields for min and max values
const minField = reactive<FieldValidation<number | undefined>>({
  value: undefined,
});
const maxField = reactive<FieldValidation<number | undefined>>({
  value: undefined,
});

onMounted(() => {
  if (props.useValidator) {
    // Initialize minField and maxField with vee-validate
    Object.assign(
      minField,
      useField(
        props.minFieldName ?? 'minNumberInput',
        (value: number | undefined) => {
          return setValidatorMessage(value);
        },
        { initialValue: undefined },
      ),
    );

    Object.assign(
      maxField,
      useField(
        props.maxFieldName ?? 'maxNumberInput',
        (value: number | undefined) => {
          return setValidatorMessage(value);
        },
        { initialValue: undefined },
      ),
    );

    // Check if props.value is not null or undefined
    if (props.value) {
      minField.value = props.value[0];
      maxField.value = props.value[1];
    }
  }

  if (props.invalid && props.useValidator) {
    minField.validate();
    maxField.validate();
  }
});

const invalidInput = computed<boolean>(
  () => props.invalid || !!minField.errorMessage || !!maxField.errorMessage,
);

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Enter') emit('submit');
};

const setValidatorMessage = async (
  value: number | undefined,
): Promise<boolean | string> => {
  await nextTick();

  if (typeof props.validatorMessage === 'string' && props.invalid) {
    return props.validatorMessage;
  } else if (typeof props.validatorMessage !== 'string') {
    const { empty } = props.validatorMessage ?? {};
    if (value == null && props.mandatory) {
      return empty ?? true;
    }
  }

  return true;
};

// Watch for changes in minField and maxField
watch(
  [
    (): number | undefined => minField.value,
    (): number | undefined => maxField.value,
  ],
  ([newMinValue, newMaxValue]) => {
    emit('update:modelValue', [newMinValue, newMaxValue]);
  },
);

// Watch for changes in props.value (for async data)
watch(
  () => props.value,
  (value) => {
    minField.value = value ? value[0] : undefined;
    maxField.value = value ? value[1] : undefined;
  },
  { once: true },
);

// Watch for changes in props.modelValue
watch(
  () => props.modelValue,
  (model) => {
    minField.value = model ? model[0] : undefined;
    maxField.value = model ? model[1] : undefined;
  },
);
</script>

<template>
  <FieldWrapper :label="props.label" :show-optional-text="showOptionalText">
    <div class="flex gap-2 w-full items-center" data-wv-section="inputwrapper">
      <InputGroup :invalid="invalidInput">
        <InputNumber
          v-bind="$props"
          v-model="minField.value"
          :max="maxField.value"
          :placeholder="placeholder ?? minPlaceholder ?? locale.minPlaceholder"
          :use-grouping="false"
          @keydown="handleKeydown"
          class="w-full"
          input-id="range-start"
        />
      </InputGroup>
      -
      <InputGroup :invalid="invalidInput">
        <InputNumber
          v-bind="$props"
          v-model="maxField.value"
          :min="minField.value"
          :placeholder="placeholder ?? maxPlaceholder ?? locale.maxPlaceholder"
          :use-grouping="false"
          @keydown="handleKeydown"
          class="w-full"
          input-id="range-end"
        />
      </InputGroup>
    </div>

    <ValidatorMessage
      :message="minField.errorMessage ?? maxField.errorMessage"
    />
  </FieldWrapper>
</template>
